"use client";

import React, { useState } from "react";
import { LuChevronDown } from "react-icons/lu";
import { Popover, PopoverTrigger, PopoverContent } from "../Popover";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { Typography } from "../Typography";
import { LanguageSelectorProps } from "./types";
import { cn } from "../../lib/utils";

const classes = {
  trigger: "w-auto h-9 text-primary p-0 hover:bg-gray-100 rounded-full px-2 transition-colors",
  iconContainer: "flex-shrink-0 w-5 h-5",
  item: "py-3 px-3 transition-colors cursor-pointer rounded-md hover:bg-red-500",
};

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage,
  languages,
  onLanguageChange,
  triggerClassName,
  contentClassName,
  itemClassName,
  placeholder = "Select Language",
}) => {
  const [open, setOpen] = useState(false);

  const selectedLanguageDetails = languages.find(
    (lang) => lang.value === currentLanguage
  );

  const handleLanguageSelect = (languageValue: string) => {
    onLanguageChange(languageValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="toolbar"
          className={cn(classes.trigger, triggerClassName)}
          aria-expanded={open}
        >
          <Flex align="center" gap={2}>
            {selectedLanguageDetails ? (
              <Flex align="center" gap={2}>
                <div className="flex-shrink-0 w-[25px] h-5 flex items-center justify-center">
                  {selectedLanguageDetails.icon}
                </div>
                <Typography
                  variant="label"
                  size="sm"
                  className="whitespace-nowrap hidden xl:flex dark:text-white"
                  color="gray600"
                >
                  {selectedLanguageDetails.label}
                </Typography>
              </Flex>
            ) : (
              <Typography
                variant="default"
                size="sm"
                className="w-full text-center md:text-left"
              >
                {placeholder}
              </Typography>
            )}
            <LuChevronDown className="h-4 w-4 ml-auto text-ticketon-dark-gray dark:text-white" />
          </Flex>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("p-1 w-auto md:w-auto min-w-[200px]", contentClassName)}
        align="center"
        side="bottom"
        sideOffset={5}
      >
        {languages.map((language) => (
          <Flex
            key={language.value}
            onClick={() => handleLanguageSelect(language.value)}
            align="center"
            gap={3}
            className={cn(
              classes.item,
              "hover:bg-gray-50",
              currentLanguage === language.value && "bg-gray-100",
              itemClassName,
            )}
          >
            <Flex
              align="center"
              justify="center"
            >
              {language.icon}
            </Flex>
            <Typography
              variant="label"
              size="sm"
              weight="medium"
            >
              {language.label}
            </Typography>
          </Flex>
        ))}
      </PopoverContent>
    </Popover>
  );
};

LanguageSelector.displayName = "LanguageSelector";
